/**
 * Handles web/PWA push notification registration and foreground display.
 * Mirrors NativePushHandler but for non-native web. Fully no-ops unless the
 * Firebase web config env vars are present (handled inside initWebPush).
 * Only active when authenticated as member, trainer, or administrator on web.
 */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { initWebPush, cleanupWebPush } from '../utils/webPush';
import { selectIsAuthenticated, selectUserRole } from '../store/slices/authSlice';

export default function WebPushHandler() {
  const { i18n } = useTranslation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!isAuthenticated || (userRole !== 'member' && userRole !== 'trainer' && userRole !== 'administrator')) {
      return;
    }

    const language = i18n.language?.split('-')[0] || 'hr';

    initWebPush({
      language,
      onMessage: (payload) => {
        const n = payload?.notification || payload?.data || {};
        const title = n.title || '';
        const body = n.body || '';
        if (title || body) {
          toast(title ? (body ? `${title}: ${body}` : title) : body, {
            id: 'web-push-foreground',
            duration: 5000,
            icon: '🔔',
          });
        }
      },
    });

    return () => {
      cleanupWebPush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userRole]);

  return null;
}
